import React from 'react'
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { t } from 'ttag'
import { CHALLENGE_ADMIN_STEP_ACTIVITY_TYPE, FormData } from '../../ChallengeAdmin'
import { activityTypes } from '../../activityTypes'
import Typography from '@material-ui/core/Typography'
import { List, ListItem, ListItemIcon, ListItemText, TextField } from '@material-ui/core'
import { CustomTypeCategory } from '../../../../services/CustomTypes'
import { Capacitor } from '@capacitor/core'
import { scrollToBottom } from '../../../../common/helpers/scroll'
import { YumActivityIcon } from '../../../../components/YumActivityIcon/YumActivityIcon'
import YumMediumTitle from '../../../../components/YumMediumTitle/YumMediumTitle'
import CustomUnitInput from '../ChallengeStepGoal/fields/CustomUnitInput'
import { getTotalAmountGoalValidationRules } from '../ChallengeStepGoal/goalValidationHelpers'
import { customTypes } from '../../../../services/CustomTypes'
import ErrorMessage from '../../../../components/ErrorMessage/ErrorMessage'
import { getErrorMessage } from '../ChallengeStepGoal/goalFormHelpers'

interface ChallengeStepPeriodProps {
  setValue: UseFormSetValue<FormData>
  register: UseFormRegister<FormData>
  errors: FieldErrors<FormData>
  formStep: number
  activityType: CustomTypeCategory
  control: Control<FormData>
  watch: UseFormWatch<FormData>
}

export default function ChallengeStepActivityType(props: ChallengeStepPeriodProps) {
  const { setValue, register, errors, formStep, activityType, control, watch } = props

  const activityTypesClone = activityTypes.slice()

  const filteredActivityTypes = activityTypesClone.filter(
    (a) => ![CustomTypeCategory.POINTS, CustomTypeCategory.TOTAL_POINTS].includes(a.value),
  )
  const filteredPointTypes = activityTypesClone.filter((a) =>
    [CustomTypeCategory.POINTS, CustomTypeCategory.TOTAL_POINTS].includes(a.value),
  )
  const filteredMostPopularTypes = filteredActivityTypes.filter(
    (a) =>
      [CustomTypeCategory.STEPS, CustomTypeCategory.DISTANCE, CustomTypeCategory.EXERCISE].includes(a.value) &&
      a.usesIntegration,
  )
  const filteredOtherAutomatedTypes = filteredActivityTypes.filter(
    (a) =>
      ![CustomTypeCategory.STEPS, CustomTypeCategory.DISTANCE, CustomTypeCategory.EXERCISE].includes(a.value) &&
      a.usesIntegration,
  )

  const setActivityTypeValue = (value: any) => {
    setValue('activityType', value)

    if (value === CustomTypeCategory.CUSTOM) {
      setTimeout(() => {
        scrollToBottom()
      }, 0)
    }
  }

  const activityTypesListItems = (
    {
      label,
      value,
      description,
    }: {
      label: string
      value: any
      description?: string
    },
    index: number,
  ) => {
    const labelId = `type-${index}`
    return (
      <ListItem
        key={index}
        role={undefined}
        button
        disableRipple
        selected={activityType === value}
        onClick={() => setActivityTypeValue(value)}
      >
        <ListItemIcon>
          {activityType === value ? (
            <YumActivityIcon category={value} />
          ) : (
            <YumActivityIcon category={value} color={'darkBlue'} isInactive={true} />
          )}
        </ListItemIcon>
        <ListItemText
          id={labelId}
          primary={label}
          secondary={description}
          style={description ? { marginTop: '0px', marginBottom: '0px' } : {}}
          secondaryTypographyProps={{ style: { fontSize: '14px' } }}
        />
      </ListItem>
    )
  }

  const manualEntryTypes = filteredActivityTypes
    .filter(({ usesIntegration, value }) => !usesIntegration && value !== CustomTypeCategory.CUSTOM)
    .sort((a, b) => a.label.localeCompare(b.label))

  return (
    <div style={{ marginBottom: 10 }}>
      <YumMediumTitle>{t`Most popular`}</YumMediumTitle>
      <List>{filteredMostPopularTypes.map(activityTypesListItems)}</List>

      <YumMediumTitle>{t`Wellness points`}</YumMediumTitle>
      <List>{filteredPointTypes.map(activityTypesListItems)}</List>

      <YumMediumTitle>{t`Automated sync`}</YumMediumTitle>
      <List>
        {filteredOtherAutomatedTypes.filter(({ usesIntegration }) => usesIntegration).map(activityTypesListItems)}
      </List>

      <YumMediumTitle>{t`Manual entry`}</YumMediumTitle>
      <List>{manualEntryTypes.map(activityTypesListItems)}</List>

      <YumMediumTitle>{`${t`Custom`} (${t`Manual entry`.toLowerCase()})`}</YumMediumTitle>

      <List>
        {filteredActivityTypes.filter(({ value }) => value == CustomTypeCategory.CUSTOM).map(activityTypesListItems)}
      </List>

      {activityType === CustomTypeCategory.CUSTOM && (
        <>
          <div style={{ marginBottom: '12px' }}>
            <TextField
              style={{ marginTop: 0, width: '100%', marginBottom: '0' }}
              label={t`Enter activity name`}
              {...register('customActivityName', { required: formStep === CHALLENGE_ADMIN_STEP_ACTIVITY_TYPE })}
              margin="normal"
              error={!!errors.customActivityName}
              variant="filled"
              InputProps={{
                disableUnderline: true,
              }}
              inputProps={{
                id: 'customActivityName',
                // This disables the browser autocomplete.
                autoComplete: 'new-password',
              }}
              autoFocus={Capacitor.getPlatform() === 'web'}
            />
            {errors.customActivityName && getErrorMessage(errors.customActivityName as any) && (
              <ErrorMessage>{getErrorMessage(errors.customActivityName as any)}</ErrorMessage>
            )}
          </div>
          {/* Custom unit input field - shown when CUSTOM activity type is selected */}
          <CustomUnitInput
            control={control}
            fieldPrefix=""
            validationRules={{ required: activityType === CustomTypeCategory.CUSTOM }}
          />
          {/* {(() => {
            // Get form values needed for validation
            const simpleManualEntry = watch('simpleManualEntry')
            const goalType = watch('goalType')
            const isDefinedCustomActivity = customTypes.some((ct) => ct.id === activityType)

            // Get validation rules for custom unit field
            // At activity type step, validation is optional (only required at goal step)
            const validationRules = getTotalAmountGoalValidationRules(
              formStep,
              activityType,
              goalType || 'totalAmount',
              isDefinedCustomActivity,
              simpleManualEntry || false,
            )

            return <CustomUnitInput control={control} fieldPrefix="" validationRules={validationRules.customUnit} />
          })()} */}
        </>
      )}
    </div>
  )
}
